import React, {useContext, useEffect, useState} from "react";
import Placeholder from "../ui-components/Placeholder";
import {FormattedMessage, useIntl} from "react-intl";
import Input from "../ui-components/Input";
import Select from "../ui-components/Select";
import {
  CheckCircleIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon
} from "@heroicons/react/24/outline";
import Button from "../ui-components/Button";
import Toggle from "../ui-components/Toggle";
import {NotificationsContext} from "../ui-components/Notifications";


const SORT_OPTIONS = [
  {name: 'show_title_desc', value: 'desc'},
  {name: 'show_title_asc', value: 'asc'},
]


const ServiceListing = ({
    edits,
    setEdits,
    onFetch=async () => {},
    onClone=async (_) => {},
    onOpen=(_) => {},
    creatingNew=false,
    pageSize=10,
    cloneSuccessMessageId="",
    noItemsMessageId="",
}) => {

  // context
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();

  // state
  // -- data
  const [items, setItems] = useState(null);
  // -- filtering
  const [sortMode, setSortMode] = useState("desc");
  const [search, setSearch] = useState("")
  const [activeOnly, setActiveOnly] = useState(false);
  // -- paging
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  // -- extra
  const [cloning, setCloning] = useState(null);

  // utilities
  const pages = Array.from(Array(Math.ceil(total / pageSize)).keys());
  const cloneItem = async (i) => {
    setCloning(i.id);
    try {
      await onClone(i);
      cloneSuccessMessageId && push({type: 'success', title: intl.formatMessage({id: cloneSuccessMessageId})});
      setEdits((prev) => prev + 1);
    }
    catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})})
      throw error;
    }
    finally {
      setCloning(null);
    }
  }

  useEffect(() => {
    if (creatingNew !== false) {
      const pageGetterFn = sortMode === 'asc' ? Math.min : Math.max;
      setPage(Math.max(pageGetterFn(...pages), 0));
      return;
    }

    const fetch = async () => {
      const {rows, total} = await onFetch(search, page, sortMode, activeOnly, true);
      setItems(rows);
      setTotal(total);
    }

    // noinspection JSIgnoredPromiseFromCall
    fetch();
  }, [creatingNew, search, page, sortMode, activeOnly, edits]); // eslint-disable-line react-hooks/exhaustive-deps

  let content;
  if (!items) {
    content = Array(3)
      .fill(null)
      .map((_, idx) => <Placeholder key={idx} classNames="rounded-full h-12 mb-4" />);
  }
  else if (items.length === 0) {
    content = <div className="flex items-center justify-center rounded-2xl bg-gray-100 w-full h-40">
      <FormattedMessage id={noItemsMessageId} />
    </div>
  }
  else {
    content = <>
      {/* --------------- Items --------------- */}
      <div className="flex flex-col gap-4 my-2">
        {items.map((i, idx) =>
          <div
            key={idx}
            className="flex justify-between px-4 py-2 border border-black gap-2"
          >
            <h2 className="text-2xl flex items-center gap-3">
              <span className="cursor-pointer" onClick={() => { onOpen(i); }}>
                {i.des_title}
              </span>
              {!!i.flg_active && <CheckCircleIcon className="text-green-400 w-6 flex-shrink-0" />}
              <Button
                styleType="blue"
                textCls="text-xs"
                paddingCls="py-1 px-3"
                onClick={async () => { await cloneItem(i); }}
                submitting={cloning === i.id}
              >
                <DocumentDuplicateIcon className="w-4" />
                <FormattedMessage id="clone" />
              </Button>
            </h2>
            <ChevronRightIcon className="w-6 cursor-pointer flex-shrink-0" onClick={() => { onOpen(i); }} />
          </div>
        )}
      </div>
      {/* --------------- Pagination --------------- */}
      <div className="w-full mt-6 flex items-center gap-3 justify-center flex-wrap">
        {pages.map((idx) =>
          <Button
            key={idx}
            styleType={page === idx ? 'default' : 'empty'}
            onClick={() => {
              if (page !== idx)
                setPage(idx);
            }}
            paddingCls="px-2 py-1.5"
          >
            {idx+1}
          </Button>
        )}
      </div>
    </>
  }

  return <>
    {/* --------------- Controls --------------- */}
    <div className="flex flex-col sm:flex-row gap-x-4 mb-4 sm:mb-0">
      <div className="flex items-start gap-4 flex-1">
        <MagnifyingGlassIcon className="w-6 my-2.5" />
        <div className="flex-1">
          <Input
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
            placeholder={intl.formatMessage({id: "search"})}
          />
        </div>
      </div>
      <div className="flex items-start gap-4">
        <Select
          value={sortMode}
          options={SORT_OPTIONS.map((o) => ({...o, name: intl.formatMessage({id: o.name})}))}
          onChange={(evt) => { setSortMode(evt.target.value); }}
        />
        <Toggle
          label={intl.formatMessage({id: 'show_active_only'})}
          checked={activeOnly}
          onChange={() => { setActiveOnly((prev) => !prev); }}
          marginCls="mt-1"
        />
      </div>
    </div>

    {/* --------------- Content --------------- */}
    {content}
  </>
}

export default ServiceListing;